// Centralised per-route head-tag injection for the GYMPODS SPA.
//
// Replaces the three divergent `ensureMeta` helpers that used to live inline
// in HomeView / PutneyView / DalstonView / TrainersView / PricingView /
// FAQsView. The previous Home/Putney/Dalston pattern guarded creation behind
// `if (!document.querySelector(...))` and silently skipped overwrites — so the
// homepage canonical leaked into every route on SPA navigation.
//
// This helper always removes any matching tag before inserting a fresh one,
// so per-route values are authoritative. JSON-LD is wholesale-replaced on
// every call to guarantee schema from a previously-mounted route never
// leaks into <head>.
//
// `og:site_name` and `og:locale` are deliberately NOT set here — they sit
// in `public/index.html` as static defaults that apply sitewide.

const SITE_ORIGIN = 'https://gympods.com';
const DEFAULT_OG_IMAGE_PATH = '/img/og-home.jpg';
// Sitewide robots default — mirrors the value baked into public/index.html
// so per-route applySeoHead calls that omit `cfg.robots` leave the DOM in
// the same state as a cold page load. Without this, client-side nav away
// from a `noindex` route (e.g. /offices → /putney) would drop the default
// entirely because setMeta unconditionally removes the existing robots
// meta before inserting, but only ran when cfg.robots was set.
const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

export function applySeoHead(cfg) {
  const created = [];
  // `path` is optional: views with no canonical URL of their own (e.g.
  // NotFoundView, served for arbitrary unknown paths) omit it, and then
  // no canonical / og:url is emitted at all — never "https://gympods.com/undefined"
  // and never a fallback to `/`.
  const url = cfg.path ? SITE_ORIGIN + cfg.path : null;
  const ogImage = SITE_ORIGIN + (cfg.ogImage || DEFAULT_OG_IMAGE_PATH);
  const ogTitle = cfg.ogTitle || cfg.title;
  const ogDescription = cfg.ogDescription || cfg.description;

  document.title = cfg.title;

  setMeta('name', 'description', cfg.description, created);
  // Always set robots so client-side nav from a noindex route back to
  // an indexable one restores the sitewide default rather than leaving
  // the DOM with no robots meta at all. See DEFAULT_ROBOTS above.
  setMeta('name', 'robots', cfg.robots || DEFAULT_ROBOTS, created);
  if (url) replaceLink('canonical', url, created);
  else removeExisting('link[rel="canonical"]');

  setMeta('property', 'og:title', ogTitle, created);
  setMeta('property', 'og:description', ogDescription, created);
  if (url) setMeta('property', 'og:url', url, created);
  else removeExisting('meta[property="og:url"]');
  setMeta('property', 'og:type', 'website', created);
  setMeta('property', 'og:image', ogImage, created);
  setMeta('property', 'og:image:width', '1200', created);
  setMeta('property', 'og:image:height', '630', created);
  if (cfg.ogImageAlt) setMeta('property', 'og:image:alt', cfg.ogImageAlt, created);

  setMeta('name', 'twitter:card', 'summary_large_image', created);
  setMeta('name', 'twitter:title', cfg.twitterTitle || ogTitle, created);
  setMeta('name', 'twitter:description', cfg.twitterDescription || ogDescription, created);
  setMeta('name', 'twitter:image', ogImage, created);
  if (cfg.ogImageAlt) setMeta('name', 'twitter:image:alt', cfg.ogImageAlt, created);

  document.querySelectorAll('script[type="application/ld+json"]').forEach(n => n.remove());
  (cfg.jsonLd || []).forEach(block => addJsonLd(block, created));

  return created;
}

export function removeSeoHead(elements) {
  if (!elements) return;
  elements.forEach(el => el.parentNode && el.parentNode.removeChild(el));
}

// Append an extra JSON-LD block AFTER applySeoHead has already run.
// Used by views that build schema asynchronously (e.g. TrainersView's
// ItemList<Person> after the trainer roster fetch resolves). The new
// element is pushed to the same elements array so it cleans up with
// removeSeoHead on unmount.
export function appendJsonLd(data, elements) {
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
  if (elements) elements.push(s);
  return s;
}

function removeExisting(selector) {
  document.querySelectorAll(selector).forEach(n => n.remove());
}

function setMeta(attr, value, content, created) {
  document.querySelectorAll(`meta[${attr}="${value}"]`).forEach(n => n.remove());
  const m = document.createElement('meta');
  m.setAttribute(attr, value);
  m.setAttribute('content', content);
  document.head.appendChild(m);
  created.push(m);
}

function replaceLink(rel, href, created) {
  document.querySelectorAll(`link[rel="${rel}"]`).forEach(n => n.remove());
  const l = document.createElement('link');
  l.setAttribute('rel', rel);
  l.setAttribute('href', href);
  document.head.appendChild(l);
  created.push(l);
}

function addJsonLd(data, created) {
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
  created.push(s);
}
